import React, { useEffect, useMemo, useState } from "react";
import { AlertDialog, Badge, Box, Button, Callout, Card, Dialog, Flex, Grid, IconButton, SegmentedControl, Select, Separator, Text, TextField, Tooltip } from "@radix-ui/themes";
import { ChatBubbleIcon, EnvelopeClosedIcon, InfoCircledIcon, MobileIcon, PlusIcon, ReloadIcon, TrashIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { ToastAlert } from "components";
import { useProfileContext } from "context";
import { useGetWhatsappCredentialAccounts, useMarketingWhatsappTemplates } from "hooks";
import { useToast } from "../../context/ToastContext";
import MarketingWhatsappPreview from "./MarketingWhatsappPreview";
import WhatsappTemplateQualityHelp from "./WhatsappTemplateQualityHelp";
import {
  getWhatsappTemplateStatusLabel,
  WHATSAPP_TEMPLATE_LANGUAGES,
  WHATSAPP_TEMPLATE_STATUS_COLORS,
} from "./marketingWhatsappStore";

const WHATSAPP_TEMPLATE_INTEGRATION_REQUIRED_MESSAGE = "Connect a WhatsApp integration before creating WhatsApp templates.";

function ChannelFilter({ selectedSegment, onSelectedSegmentChange }) {
  return (
    <SegmentedControl.Root value={selectedSegment} onValueChange={onSelectedSegmentChange}>
      <SegmentedControl.Item value="all">All</SegmentedControl.Item>
      <SegmentedControl.Item value="email">
        <Flex gap="1" align="center">
          <EnvelopeClosedIcon />
          <Text>Email</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="sms">
        <Flex gap="1" align="center">
          <MobileIcon />
          <Text>SMS</Text>
        </Flex>
      </SegmentedControl.Item>
      <SegmentedControl.Item value="whatsapp">
        <Flex gap="1" align="center">
          <ChatBubbleIcon />
          <Text>Whatsapp</Text>
        </Flex>
      </SegmentedControl.Item>
    </SegmentedControl.Root>
  );
}

export default function MarketingWhatsappTemplatesPanel({ selectedSegment, onSelectedSegmentChange }) {
  const navigate = useNavigate();
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedWhatsappConfigId, setSelectedWhatsappConfigId] = useState("");
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isRefreshingStatuses, setIsRefreshingStatuses] = useState(false);
  const { data: whatsappAccountsData } = useGetWhatsappCredentialAccounts(userProfile?.organisation_id);

  const {
    whatsappTemplates: templates,
    createTemplateAsync,
    deleteTemplateAsync,
    refreshTemplateStatusAsync,
    isCreating,
    isLoading,
  } = useMarketingWhatsappTemplates(userProfile?.organisation_id);
  const whatsappAccounts = useMemo(() => whatsappAccountsData?.data?.accounts || [], [whatsappAccountsData]);
  const hasWhatsappIntegration = whatsappAccounts.length > 0;
  const refreshableTemplates = useMemo(() => templates.filter((template) => template.metaTemplateId), [templates]);

  useEffect(() => {
    if (isCreateOpen && !selectedWhatsappConfigId && whatsappAccounts.length === 1) {
      setSelectedWhatsappConfigId(String(whatsappAccounts[0].id));
    }
  }, [isCreateOpen, selectedWhatsappConfigId, whatsappAccounts]);

  const handleCreateOpenChange = (open) => {
    if (open && !hasWhatsappIntegration) {
      setToastMessage(WHATSAPP_TEMPLATE_INTEGRATION_REQUIRED_MESSAGE);
      return;
    }

    setIsCreateOpen(open);
    if (!open) {
      setNewTemplateName("");
      setSelectedLanguage("");
      setSelectedWhatsappConfigId("");
    }
  };

  const handleCreateTemplate = async () => {
    const cleanedName = newTemplateName.trim();
    if (!cleanedName) {
      setToastMessage("Add a template name before creating it");
      return;
    }

    if (!userProfile?.organisation_id) {
      setToastMessage("There was an error. Please try again.");
      return;
    }

    if (!selectedWhatsappConfigId) {
      setToastMessage("Select a WhatsApp account before creating the template");
      return;
    }

    if (!selectedLanguage) {
      setToastMessage("Select a language before creating the template");
      return;
    }

    try {
      const template = await createTemplateAsync({
        organisation: userProfile.organisation_id,
        whatsapp_config: selectedWhatsappConfigId,
        name: cleanedName,
        language: selectedLanguage,
        header_enabled: true,
        header_type: "text",
        header_text: cleanedName,
        body: "",
        footer_enabled: false,
        footer_text: "",
        buttons: [],
      });
      setNewTemplateName("");
      setSelectedLanguage("");
      setSelectedWhatsappConfigId("");
      setIsCreateOpen(false);
      navigate(`/marketing/templates/whatsapp/${template.id}`, { state: { template } });
    } catch (error) {
      setToastMessage("Unable to create the WhatsApp template right now");
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    try {
      await deleteTemplateAsync(templateToDelete.id);
      setTemplateToDelete(null);
      setToastMessage("WhatsApp template removed");
    } catch (error) {
      setToastMessage("Unable to delete WhatsApp template right now");
    }
  };

  const handleRefreshStatuses = async () => {
    if (refreshableTemplates.length === 0) {
      setToastMessage("No submitted WhatsApp templates to refresh");
      return;
    }

    setIsRefreshingStatuses(true);
    setToastMessage("Refreshing WhatsApp template statuses and quality scores from Meta. This can take a moment.");
    const results = await Promise.allSettled(
      refreshableTemplates.map((template) => refreshTemplateStatusAsync(template.id))
    );
    setIsRefreshingStatuses(false);

    const failedCount = results.filter((result) => result.status === "rejected").length;
    if (failedCount) {
      setToastMessage(`${failedCount} WhatsApp template${failedCount === 1 ? "" : "s"} could not be refreshed`);
      return;
    }

    setToastMessage("WhatsApp template statuses and quality scores refreshed");
  };

  return (
    <>
      <Flex
        direction="column"
        gap="5"
        style={{
          alignSelf: "stretch",
          alignItems: "center",
          flex: "1 0 0",
          width: "100%",
          minHeight: 0,
          overflowY: "auto",
          paddingRight: 4,
        }}
      >
        <Flex justify="between" align="center" style={{ alignSelf: "stretch" }}>
          <ChannelFilter selectedSegment={selectedSegment} onSelectedSegmentChange={onSelectedSegmentChange} />
          <Flex gap="2" align="center">
            <Button
              variant="soft"
              color="gray"
              onClick={handleRefreshStatuses}
              disabled={isRefreshingStatuses || isLoading || refreshableTemplates.length === 0}
            >
              <ReloadIcon width="16" height="16" />
              Refresh statuses
            </Button>
            {!hasWhatsappIntegration ? (
              <Tooltip content={WHATSAPP_TEMPLATE_INTEGRATION_REQUIRED_MESSAGE}>
                <span style={{ display: "inline-flex" }}>
                  <Button variant="soft" disabled>
                    <PlusIcon width="16" height="16" />
                    Create template
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button variant="soft" onClick={() => setIsCreateOpen(true)}>
                <PlusIcon width="16" height="16" />
                Create template
              </Button>
            )}
          </Flex>
        </Flex>

        <Callout.Root color="gray" variant="surface" size="1" style={{ alignSelf: "stretch" }}>
          <Callout.Icon>
            <InfoCircledIcon />
          </Callout.Icon>
          <Callout.Text>
            <WhatsappTemplateQualityHelp size="1" />
          </Callout.Text>
        </Callout.Root>

        <Flex direction="column" gap="4" style={{ alignSelf: "stretch", width: "100%", flex: "1 1 0", minHeight: 0 }}>
          {isLoading && templates.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="2"
              style={{
                alignSelf: "stretch",
                flex: "1 1 0",
                border: "1px dashed var(--gray-a6)",
                borderRadius: "var(--radius-4)",
                minHeight: 0,
                padding: "var(--space-6)",
              }}
            >
              <Text size="4" weight="medium">
                Loading WhatsApp templates...
              </Text>
            </Flex>
          ) : templates.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="2"
              style={{
                alignSelf: "stretch",
                flex: "1 1 0",
                border: "1px dashed var(--gray-a6)",
                borderRadius: "var(--radius-4)",
                minHeight: 0,
                padding: "var(--space-6)",
              }}
            >
              <Text size="4" weight="medium">
                No WhatsApp templates available.
              </Text>
              <Text size="2" color="gray" align="center">
                Create your first WhatsApp marketing template to get started.
              </Text>
            </Flex>
          ) : (
            <Grid columns="3" gap="4" style={{ alignSelf: "stretch", width: "100%", paddingBottom: "var(--space-2)" }}>
              {templates.map((template) => {
                const reviewStatus = template.reviewStatus || "draft";
                const statusLabel = getWhatsappTemplateStatusLabel(reviewStatus, template.qualityScore);

                return (
                  <Card
                    key={template.id}
                    style={{ display: "flex", flexDirection: "column", padding: 0, overflow: "hidden", height: 240 }}
                  >
                    <Flex direction="column" style={{ width: "100%", height: "100%" }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/marketing/templates/whatsapp/${template.id}`, { state: { template } })}
                        style={{ all: "unset", cursor: "pointer", flex: "1 1 0", minHeight: 0 }}
                      >
                        <Box style={{ height: "100%", overflow: "hidden", background: "#e6ddd4" }}>
                          <MarketingWhatsappPreview template={template} compact />
                        </Box>
                      </button>
                      <Separator size="4" style={{ minHeight: 1 }} />
                      <Flex direction="column" gap="2" p="4">
                        <Flex justify="between" align="start" gap="3">
                          <Flex direction="column" gap="2" style={{ minWidth: 0 }}>
                            <Text
                              weight="medium"
                              size="2"
                              style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
                            >
                              {template.name}
                            </Text>
                            {statusLabel ? (
                              <Badge color={WHATSAPP_TEMPLATE_STATUS_COLORS[reviewStatus] || "gray"} variant="soft" style={{ alignSelf: "flex-start" }}>
                                {statusLabel}
                              </Badge>
                            ) : null}
                          </Flex>
                          <IconButton
                            size="1"
                            variant="ghost"
                            color="red"
                            onClick={() => setTemplateToDelete(template)}
                          >
                            <TrashIcon />
                          </IconButton>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Card>
                );
              })}
            </Grid>
          )}
        </Flex>
      </Flex>

      <Dialog.Root open={isCreateOpen} onOpenChange={handleCreateOpenChange}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>Create WhatsApp Template</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Name the template first, then continue in the WhatsApp editor to build the message.
          </Dialog.Description>

          <Flex direction="column" gap="3" mt="4">
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Name
              </Text>
              <TextField.Root
                placeholder="Weekend offer"
                value={newTemplateName}
                onChange={(event) => setNewTemplateName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    handleCreateTemplate();
                  }
                }}
              />
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                WhatsApp account
              </Text>
              <Select.Root value={selectedWhatsappConfigId} onValueChange={setSelectedWhatsappConfigId} disabled={whatsappAccounts.length === 0}>
                <Select.Trigger placeholder="Select account" style={{ width: "100%" }} />
                <Select.Content position="popper">
                  {whatsappAccounts.map((account) => (
                    <Select.Item key={account.id} value={String(account.id)}>
                      {account.account_name || `WhatsApp Business Account ${account.business_id}`}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">
                Language
              </Text>
              <Select.Root value={selectedLanguage} onValueChange={setSelectedLanguage}>
                <Select.Trigger placeholder="Select language" style={{ width: "100%" }} />
                <Select.Content position="popper">
                  {WHATSAPP_TEMPLATE_LANGUAGES.map((language) => (
                    <Select.Item key={language.value} value={language.value}>
                      {language.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button onClick={handleCreateTemplate} disabled={!newTemplateName.trim() || !selectedWhatsappConfigId || !selectedLanguage || isCreating}>
              Create template
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={Boolean(templateToDelete)} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
        <AlertDialog.Content style={{ maxWidth: 450 }}>
          <AlertDialog.Title>Delete WhatsApp template?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This removes the WhatsApp template from the templates list.
          </AlertDialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleDeleteTemplate}>
                Delete
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>

      <ToastAlert />
    </>
  );
}
